import React, { useCallback } from "react";
import clsx from "clsx";
import { NavLink } from "react-router-dom";
import { createStyles, makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Hidden from "@mui/material/Hidden";

import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useUserContext } from "../../../context/user/AuthContext";

import { IoIosMenu } from "react-icons/io";
import { FaAddressBook, FaFile, FaMedal, FaRegAddressBook } from "react-icons/fa";
import {
    IconWrapper, //
    SidebarIconWrapper,
    ComunitySidebarIcon,
    CourseSidebarIcon,
    DashboardSidebarIcon,
    MultimidiaSidebarIcon,
    DownloadsSidebarIcon,
    EbooksSidebarIcon,
    ArticlesSidebarIcon,
    SociautismIcon,
    PosIcon,
} from "../../elements/Icons";

import { trackGA } from "../../../helpers/trackEvent";
import { SidebarContainer } from "./SidebarStyled";
import { PresentIcon } from "../../elements/Icons/SVGIcons/Sidebar/presenticon";
import { AiOutlineStar } from "react-icons/ai";
import { getPosGraduacaoLink, parseUserGroups, hasGroup, USER_GROUPS } from "../../../constants/userGroups";

export interface Props {
    logoUrl: string;
}

const drawerWidth = 240;
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: "nowrap",
        },
        drawerOpen: {
            width: drawerWidth,
            borderColor: "#f1f1f1",
            background: "#f1f1f1",
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawerClose: {
            borderColor: "#f1f1f1",
            background: "#f1f1f1",
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: "hidden",
            width: "65px",
            [theme.breakpoints.up("sm")]: {
                width: "65px",
            },
        },
        NavLink: {
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            fontWeight: 700,
            width: "100%",
            height: "100%",
            color: "#013A7B",
            fontSize: "13px",
        },
        paper: {
            backgroundColor: "#f1f1f1",
        },
    })
);

const SidebarWrapper: React.FC<Props> = ({ ...props }: Props) => {
    const classes = (useStyles as any)();
    const { isPlus, showPos, userData } = useUserContext();
    const [open, setOpen] = React.useState(false);
    const toggleDrawer = () => {
        setOpen(!open);
    };

    const menuItems = [
        {
            name: "Início",
            link: "/dashboard",
            restrictedToSubscribers: true,
            icon: <DashboardSidebarIcon color="#013A7B" />,
        },
        {
            name: "Artigos",
            link: "/artigos",
            restrictedToSubscribers: true,
            icon: <ArticlesSidebarIcon color="#013A7B" />,
        },
        {
            name: "Notícias",
            link: "/noticias",
            restrictedToSubscribers: true,
            icon: <ComunitySidebarIcon color="#013A7B" />,
        },
        {
            name: "Cursos",
            link: "/cursos",
            restrictedToSubscribers: false,
            icon: <CourseSidebarIcon color="#013A7B" />,
        },
        {
            name: "Materiais",
            link: "/materiais",
            restrictedToSubscribers: true,
            icon: <DownloadsSidebarIcon color="#013A7B" />,
        },
        {
            name: "E-books",
            link: "/ebooks",
            restrictedToSubscribers: false,
            icon: <EbooksSidebarIcon color="#013A7B" />,
        },
        {
            name: "Séries",
            link: "/multimidias",
            restrictedToSubscribers: true,
            icon: <MultimidiaSidebarIcon color="#013A7B" />,
        }
    ];

    const restrictedItem = useCallback((item: any) => {
        if (item.restrictedToSubscribers) {
            if (isPlus()) {
                return true;
            } else {
                return false;
            }
        } else {
            return true;
        }
    }, []);

    const posLink = () => getPosGraduacaoLink(userData.grupos);

    // Helper to check if user has a specific group
    const userHasGroup = (group: string) => {
        const userGroups = parseUserGroups(userData.grupos);
        return hasGroup(userGroups, group);
    };

    return (
        <SidebarContainer open={open}>
            <Hidden mdUp>
                <div className={isPlus() ? "handleDrawer isPlus" : "handleDrawer"} onClick={() => toggleDrawer()}>
                    <IconWrapper filled={false} icon={<IoIosMenu size="30px" />} />
                </div>
                <Drawer classes={{ paper: classes.paper }} variant="temporary" open={open} onClick={() => toggleDrawer()}>
                    <List>
                        {menuItems.map((item, index) => (
                            <React.Fragment key={index}>
                                {restrictedItem(item) ? (
                                    <>
                                        <ListItem key={item.name}>
                                            <NavLink
                                                to={item.link}
                                                className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                                onClick={() => trackGA("nav", "click", item.name)}>
                                                <ListItemIcon>
                                                    <SidebarIconWrapper svgIcon={item.icon} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.name}
                                                    secondary={item.restrictedToSubscribers && item.name !== "Início" ? "Plus" : ""}
                                                    secondaryTypographyProps={{ style: { color: "#f2c94c", fontWeight: "bold" } }}
                                                />
                                            </NavLink>
                                        </ListItem>
                                    </>
                                ) : (
                                    ""
                                )}
                            </React.Fragment>
                        ))}
                        <ListItem>
                            <NavLink
                                to="/favoritos"
                                className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                onClick={() => trackGA("nav", "click", "favoritos")}>
                                <ListItemIcon>
                                    <SidebarIconWrapper svgIcon={<AiOutlineStar size={18} color="#f2c94c" />} />
                                </ListItemIcon>
                                <ListItemText primary="Favoritos" />
                            </NavLink>
                        </ListItem>
                        {showPos() && (
                            <ListItem>
                                <NavLink
                                    to={posLink()}
                                    className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                    onClick={() => trackGA("nav", "click", "Pós-Graduação")}>
                                    <ListItemIcon>
                                        <SidebarIconWrapper svgIcon={<PosIcon />} />
                                    </ListItemIcon>
                                    <ListItemText primary="Pós Graduação" />
                                </NavLink>
                            </ListItem>
                        )}
                        {!isPlus() && (
                            <ListItem>
                                <NavLink
                                    to="/seja-plus"
                                    className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                    onClick={() => trackGA("nav", "click", "Seja Plus")}>
                                    <ListItemIcon>
                                        <SidebarIconWrapper svgIcon={<FaMedal size={16} color="#013A7B" />} />
                                    </ListItemIcon>
                                    <ListItemText primary="Seja Plus" />
                                </NavLink>
                            </ListItem>
                        )}
                        {userHasGroup(USER_GROUPS.BANCO_DE_TALENTOS) && (
                            <ListItem>
                                <NavLink
                                    to="/banco-de-talentos"
                                    className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                    onClick={() => trackGA("nav", "click", "Seja Plus")}>
                                    <ListItemIcon>
                                        <SidebarIconWrapper svgIcon={<FaRegAddressBook size={16} color="#013A7B" />} />
                                    </ListItemIcon>
                                    <ListItemText primary="Banco de Talentos" />
                                </NavLink>
                            </ListItem>
                        )}
                        {(userHasGroup(USER_GROUPS.SOCIAUTISM) || isPlus()) && (
							<ListItem>
								<NavLink to='/sociautism' className={({ isActive }) => `${classes.NavLink} ${isActive ? 'active' : ''}`} onClick={() => trackGA("nav", "click", "Sociautism")}>
									<ListItemIcon>
										<SidebarIconWrapper svgIcon={<SociautismIcon color='#013A7B' />} />
									</ListItemIcon>
									<ListItemText
										primary='Sociautism'
										secondaryTypographyProps={{ style: { color: "#f2c94c", fontWeight: "bold" } }}
										secondary={isPlus() ? "Plus" : ""}
									/>
								</NavLink>
							</ListItem>
						)}
                    </List>
                </Drawer>
            </Hidden>

            <Hidden smDown>
                <Drawer
                    variant="permanent"
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}
                    className={clsx(classes.drawer, {
                        [classes.drawerOpen]: open,
                        [classes.drawerClose]: !open,
                    })}
                    classes={{
                        paper: clsx({
                            [classes.drawerOpen]: open,
                            [classes.drawerClose]: !open,
                        }),
                    }}>
                    <List>
                        {/* Sim, esse primeiro item ta aqui só pq eu não quis dar o espaço pra tirar o primeiro item de baixo do header. Me julgue. */}
                        <ListItem>
                            <NavLink to="/dashboard" className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}>
                                <ListItemIcon>
                                    <SidebarIconWrapper svgIcon={<DashboardSidebarIcon color="#013A7B" />} />
                                </ListItemIcon>
                                <ListItemText primary="Dashboard" />
                            </NavLink>
                        </ListItem>
                        {menuItems.map((item, index) => (
                            <React.Fragment key={index}>
                                {restrictedItem(item) ? (
                                    <>
                                        <ListItem key={item.name}>
                                            <NavLink
                                                to={item.link}
                                                className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                                onClick={() => trackGA("nav", "click", item.name)}>
                                                <ListItemIcon>
                                                    <SidebarIconWrapper svgIcon={item.icon} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.name}
                                                    secondary={item.restrictedToSubscribers && item.name !== "Início" ? "Plus" : ""}
                                                    secondaryTypographyProps={{ style: { color: "#f2c94c", fontWeight: "bold" } }}
                                                />
                                            </NavLink>
                                        </ListItem>
                                    </>
                                ) : (
                                    ""
                                )}
                            </React.Fragment>
                        ))}

                        <ListItem>
                            <NavLink
                                to="/favoritos"
                                className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                onClick={() => trackGA("nav", "click", "favoritos")}>
                                <ListItemIcon>
                                    <SidebarIconWrapper svgIcon={<AiOutlineStar size={18} color="#f2c94c" />} />
                                </ListItemIcon>
                                <ListItemText primary="Favoritos" />
                            </NavLink>
                        </ListItem>
                        {showPos() && (
                            <ListItem>
                                <NavLink
                                    to={posLink()}
                                    className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                    onClick={() => trackGA("nav", "click", "Pós-Graduação")}>
                                    <ListItemIcon>
                                        <SidebarIconWrapper svgIcon={<PosIcon />} />
                                    </ListItemIcon>
                                    <ListItemText primary="Pós Graduação" />
                                </NavLink>
                            </ListItem>
                        )}

                        {/* {isPlus().toString()} */}
                        {!isPlus() && (
                            <ListItem>
                                <NavLink
                                    to="/seja-plus"
                                    className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                    onClick={() => trackGA("nav", "click", "Seja Plus")}>
                                    <ListItemIcon>
                                        <SidebarIconWrapper svgIcon={<FaMedal size={16} color="#013A7B" />} />
                                    </ListItemIcon>
                                    <ListItemText primary="Seja Plus" />
                                </NavLink>
                            </ListItem>
                        )}
                        {userHasGroup(USER_GROUPS.BANCO_DE_TALENTOS) && (
                            <ListItem>
                                <NavLink
                                    to="/banco-de-talentos"
                                    className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                    onClick={() => trackGA("nav", "click", "Seja Plus")}>
                                    <ListItemIcon>
                                        <SidebarIconWrapper svgIcon={<FaRegAddressBook size={16} color="#013A7B" />} />
                                    </ListItemIcon>
                                    <ListItemText primary="Banco de Talentos" />
                                </NavLink>
                            </ListItem>
                        )}
                        {(userHasGroup(USER_GROUPS.SOCIAUTISM) || isPlus()) && (
                            <ListItem>
                                <NavLink
                                    to="/sociautism"
                                    className={({ isActive }) => `${classes.NavLink} ${isActive ? "active" : ""}`}
                                    onClick={() => trackGA("nav", "click", "Sociautism")}>
                                    <ListItemIcon>
                                        <SidebarIconWrapper svgIcon={<SociautismIcon color="#013A7B" />} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Sociautism"
                                        secondaryTypographyProps={{ style: { color: "#f2c94c", fontWeight: "bold" } }}
                                        secondary={isPlus() ? "Plus" : ""}
                                    />
                                </NavLink>
                            </ListItem>
                        )}
                        {/* userHasGroup(USER_GROUPS.SOCIAUTISM) */}
                    </List>
                </Drawer>
            </Hidden>
        </SidebarContainer>
    );
};

// SidebarWrapper.defaultProps = {
//   background: '#FFF'
// }

export { SidebarWrapper as Sidebar };
